import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './ProductDetails.css';

const ProductDetails = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active displayed main image
  const [activeMainImage, setActiveMainImage] = useState('');

  // Enlarged fullscreen image
  const [enlargedImage, setEnlargedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`https://apiqms.shigjeta.com/api/products/${productId}`);
        const productData = response.data;

        // Convert additionalImages (backend already returns array if JSON)
        let formattedAdditionalImages = [];
        if (Array.isArray(productData.additionalImages)) {
          formattedAdditionalImages = productData.additionalImages.map(img => ({
            src: img && typeof img === 'string'
              ? `https://apiqms.shigjeta.com${img}`
              : img.src && !img.src.startsWith('http')
              ? `https://apiqms.shigjeta.com${img.src}`
              : img.src || img,
            alt: img.alt || productData.name,
          }));
        }

        const updatedProduct = {
          ...productData,
          additionalImages: formattedAdditionalImages,
        };

        setProduct(updatedProduct);

        // Set initial main image
        const initialImage =
          productData.mainImage && !productData.mainImage.startsWith('http')
            ? `https://apiqms.shigjeta.com${productData.mainImage}`
            : productData.mainImage;

        setActiveMainImage(initialImage);

      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Failed to load product details.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleThumbnailClick = (imgSrc) => {
    setActiveMainImage(imgSrc);
  };

  const handleEnlargeImage = (imgSrc) => {
    setEnlargedImage(imgSrc);
  };

  const handleCloseEnlargedImage = () => {
    setEnlargedImage(null);
  };

  if (isLoading) return <div className="loading-message">Loading Product...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!product) return <div className="info-message">Product not found.</div>;

  return (
    <>
      <div className="product-detail-page">
        <div className="product-detail-container">

          {/* LEFT SIDE - Images */}
          <div className="product-image-column">
            {activeMainImage && (
              <img
                src={activeMainImage}
                alt={product.name}
                className="product-main-image"
                onClick={() => handleEnlargeImage(activeMainImage)}
              />
            )}

            {product.additionalImages && product.additionalImages.length > 0 && (
              <div className="product-additional-images">
                {product.additionalImages.map((image, index) => (
                  <img
                    key={index}
                    src={image.src}
                    alt={image.alt}
                    className="additional-image-item"
                    onClick={() => handleThumbnailClick(image.src)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* RIGHT SIDE - Info */}
          <div className="product-info-column">
            <h1 className="product-detail-title">{product.name}</h1>

            <div
              className="product-detail-description"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />

            {/* ✅ Specifications Bullet List */}
            {product.specifications?.length > 0 && (
              <div className="product-specifications">
                <h3>Specifications</h3>
                <ul>
                  {product.specifications.map((spec, index) => (
                    <li key={index} className="flex items-start gap-2 py-1">
                      <span className="font-medium">•</span>
                      <span>{spec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ENLARGE IMAGE OVERLAY */}
      {enlargedImage && (
        <div className="enlarged-image-container" onClick={handleCloseEnlargedImage}>
          <div className="enlarged-image-content">
            <img src={enlargedImage} alt="Zoomed" className="enlarged-image" />
            <button className="enlarged-image-close-btn" onClick={handleCloseEnlargedImage}>
              &times;
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetails;
